import { CloudDownload, FolderSearch } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Block, FilePicker } from './PrimaNotaBlocks';
import { SourceList } from './PrimaNotaSources';
import {
  archiveLine,
  formatName,
  slotKey,
  type ArchiveRow,
  type StatementSlot,
} from './prima-nota-archive';
import type { PeriodSource, Readiness, StatementFormat } from './prima-nota-model';

type ArchivePanelProps = {
  rows: ArchiveRow[];
  booksDay: Intl.DateTimeFormat;
  locked: boolean;
  sweeping: boolean;
  onSweep: () => void;
};

function archiveTestId(row: ArchiveRow): string {
  return row.format === null
    ? `archive-state-${row.accountKey}`
    : `archive-state-${row.accountKey}-${row.format}`;
}

/** What Drive said about each account's statement, and the button that asks it again. */
function ArchivePanel({ rows, booksDay, locked, sweeping, onSweep }: ArchivePanelProps) {
  const { t } = useTranslation('finance');
  return (
    <div
      className="bg-background border-border space-y-3 rounded-md border p-4"
      data-testid="archive-panel"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold">{t('primaNota.archive.title')}</p>
          <p className="text-muted-foreground text-[13px]">{t('primaNota.archive.hint')}</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={locked}
          data-testid="sweep-archive"
          onClick={onSweep}
        >
          <FolderSearch aria-hidden="true" />
          {sweeping ? t('primaNota.archive.sweeping') : t('primaNota.archive.sweep')}
        </Button>
      </div>
      <ul className="space-y-1 text-sm">
        {rows.map((row) => (
          <li key={`${row.accountKey}:${row.format ?? ''}`} data-testid={archiveTestId(row)}>
            <span className="font-semibold">{row.accountLabel}</span>
            {row.format !== null && (
              <span className="text-muted-foreground"> · {formatName(row.format)}</span>
            )}
            {' — '}
            {archiveLine(row.archive, t, booksDay)}
          </li>
        ))}
      </ul>
    </div>
  );
}

type StatementPickerProps = {
  slot: StatementSlot;
  busy: boolean;
  locked: boolean;
  onPick: (file: File) => void;
};

function StatementPicker({ slot, busy, locked, onPick }: StatementPickerProps) {
  const { t } = useTranslation('finance');
  const label =
    slot.format === null
      ? t('primaNota.supply.statement', { account: slot.accountLabel })
      : t('primaNota.supply.statementFormat', {
          account: slot.accountLabel,
          format: formatName(slot.format),
        });
  const hint =
    slot.format === null
      ? t('primaNota.supply.statementHint')
      : t(`primaNota.supply.statementHint_${slot.format}`);
  const testId =
    slot.format === null
      ? `upload-statement-${slot.accountKey}`
      : `upload-statement-${slot.accountKey}-${slot.format}`;
  return (
    <FilePicker
      label={label}
      hint={hint}
      choose={t('primaNota.supply.choose')}
      busy={busy}
      locked={locked}
      busyLabel={t('primaNota.supply.uploading')}
      testId={testId}
      onPick={onPick}
    />
  );
}

type SupplyBlockProps = {
  data: Readiness;
  documentsSupplied: boolean;
  archiveRows: ArchiveRow[];
  statementSlots: StatementSlot[];
  dateFormat: Intl.DateTimeFormat;
  booksDay: Intl.DateTimeFormat;
  locked: boolean;
  busy: string | null;
  withdrawing: string | null;
  withdrawReason: string;
  onUpload: (
    file: File,
    kind: PeriodSource['kind'],
    accountKey: string | null,
    format?: StatementFormat | null,
  ) => void;
  onFetchDocuments: () => void;
  onSweep: () => void;
  onStartWithdraw: (sourceId: string) => void;
  onReasonChange: (reason: string) => void;
  onWithdraw: (sourceId: string) => void;
  onCancelWithdraw: () => void;
};

function DocumentsFetch({
  locked,
  fetching,
  onFetch,
}: {
  locked: boolean;
  fetching: boolean;
  onFetch: () => void;
}) {
  const { t } = useTranslation('finance');
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-1">
      <p className="text-muted-foreground min-w-0 flex-1 text-[13px]">
        {t('primaNota.fiscalExport.hint')}
      </p>
      <Button
        variant="outline"
        size="sm"
        disabled={locked}
        data-testid="fetch-fiscal-export"
        onClick={onFetch}
      >
        <CloudDownload aria-hidden="true" />
        {fetching ? t('primaNota.fiscalExport.fetching') : t('primaNota.fiscalExport.fetch')}
      </Button>
    </div>
  );
}

/** Step two: supply what is missing, or ask for it. */
export function SupplyBlock(props: SupplyBlockProps) {
  const { data, documentsSupplied, locked, busy } = props;
  const { t } = useTranslation('finance');
  return (
    <Block id="prima-nota-supply" title={t('primaNota.steps.supply')}>
      {!documentsSupplied && (
        <FilePicker
          label={t('primaNota.supply.documents')}
          hint={t('primaNota.supply.documentsHint')}
          choose={t('primaNota.supply.choose')}
          busy={busy === 'fiscal-workbook:'}
          locked={locked}
          busyLabel={t('primaNota.supply.uploading')}
          testId="upload-fiscal-workbook"
          onPick={(file) => props.onUpload(file, 'fiscal-workbook', null)}
        />
      )}

      {!documentsSupplied && data.fiscalExportConfigured === true && (
        <DocumentsFetch
          locked={locked}
          fetching={busy === 'fiscal-export'}
          onFetch={props.onFetchDocuments}
        />
      )}

      {props.archiveRows.length > 0 && (
        <ArchivePanel
          rows={props.archiveRows}
          booksDay={props.booksDay}
          locked={locked}
          sweeping={busy === 'sweep'}
          onSweep={props.onSweep}
        />
      )}

      {props.statementSlots.map((slot) => (
        <StatementPicker
          key={`${slot.accountKey}:${slot.format ?? ''}`}
          slot={slot}
          busy={busy === slotKey('bank-statement', slot.accountKey, slot.format)}
          locked={locked}
          onPick={(file) => props.onUpload(file, 'bank-statement', slot.accountKey, slot.format)}
        />
      ))}

      <SourceList
        sources={data.sources}
        dateFormat={props.dateFormat}
        locked={locked}
        busy={busy}
        withdrawing={props.withdrawing}
        withdrawReason={props.withdrawReason}
        onStartWithdraw={props.onStartWithdraw}
        onReasonChange={props.onReasonChange}
        onWithdraw={props.onWithdraw}
        onCancelWithdraw={props.onCancelWithdraw}
      />
    </Block>
  );
}
